import { BaseElements, BaseObject, BaseService, BaseInjector } from '../../js/base';
import { querySelector, querySelectorAll, EmptyNodeList } from '../../utility/utils/index';
import bootstrapBreakpoints from '../../utility/grid/index';
import { offVPort, onVPort, ScreenSize } from '../../utility/breakPointEvents';

class Texts extends BaseObject { }

class ClassList extends BaseObject {
    hide = 'd-none';
    truncated = 'truncated';
    authorsButton = 'btn-authors-truncate';
}

class Selectors extends BaseObject {
    between = '.authors-truncate-to-hide';
    toggleTruncate = '.authors-toggle';
    authorsButton = '.btn-authors-truncate';
    truncateButtons = '.btn-authors-truncate, .authors-truncate-to-hide';
    listItems = 'li:not(.authors-toggle):not(.authors-truncate-to-hide)';
}

let $window;

class Elements extends BaseElements<Selectors> {
    allAuthorsLists: NodeListOf<HTMLElement> = new EmptyNodeList();
    currentAuthorsListItem: HTMLElement;
    truncateButton: HTMLElement;

    constructor(selectors: Selectors) {
        super(selectors, true);
    }

    initElements() {
        this.initElementsByLoop();
    }

    get toggleButton(): HTMLElement {
        return querySelector(this.selectors.authorsButton, this.currentAuthorsListItem);
    }

    get inBetweenItem(): HTMLElement {
        return querySelector(this.selectors.between, this.currentAuthorsListItem);
    }

    get listItems(): NodeListOf<HTMLElement> {
        return querySelectorAll(this.selectors.listItems, this.currentAuthorsListItem);
    }

    get showMiddleDots(): boolean {
        return !!this.currentAuthorsListItem.dataset.truncateDots;
    }

    get viewport(): { lg: string, md: string, sm: string } {
        const lg = this.currentAuthorsListItem.dataset.visibleItems;
        const md = this.currentAuthorsListItem.dataset.visibleItemsMd;
        const sm = this.currentAuthorsListItem.dataset.visibleItemsSm;
        return { lg, md, sm };
    }

    get maxLength(): number {
        const { lg, sm, md } = this.viewport;
        let maxItems = sm || md || lg;
        const width = $window.width();
        if (width > bootstrapBreakpoints.lg) {
            maxItems = lg;
        } else if (width > bootstrapBreakpoints.md) {
            maxItems = md || lg;
        } else if (width > bootstrapBreakpoints.sm) {
            maxItems = sm || md || lg;
        }
        const length = parseInt(maxItems);
        return isNaN(length) ? 0 : length - 1;
    }

}

interface AuthorsTruncate extends BaseService<Selectors, Elements, ClassList, Texts> { }

@BaseInjector(Selectors, Elements, ClassList, Texts)
class AuthorsTruncate {
    protected _inBetweenItemHTML: string;
    protected hiddenItems: number;


    get buttonText() {
        const more = this.elements.currentAuthorsListItem.dataset.truncateMoreLabel || 'authors';
        return `+${this.hiddenItems} ${more}`;
    }

    get button(): string {
        return `
    <li class="list-inline-item authors-toggle  ">
        <button class="truncated btn btn-xs btn-outline-secondary btn-authors-truncate ml-1" >${this.buttonText}</button>
    </li>`;
    }

    get inBetweenItemHTML(): string {
        return '<li class="list-inline-item authors-truncate-to-hide "><a href="#" role="button" class="toggle truncation ml-1 d-inline-block">[...]</a></li>';
    }


    get truncatedButtonText() {
        return this.elements.currentAuthorsListItem.dataset.truncateLessLabel || 'fewer';
    }


    constructor(readonly allAuthorsLists: NodeListOf<HTMLElement>) {
        $window = $(window);
    }

    initialize(): void {
        this._inBetweenItemHTML = this.inBetweenItemHTML;
        this.allAuthorsLists.forEach(item => {
            this.elements.currentAuthorsListItem = item;
            const lastAuthorsList = this.truncate();
            lastAuthorsList?.insertAdjacentHTML('afterend', this.button);
            item.querySelectorAll(this.selectors.truncateButtons).forEach(button => {
                this.elements.truncateButton = button as HTMLButtonElement
                this.addEventListeners();
            });
        });
    }

    addEventListeners() {
        const button = this.elements.truncateButton;
        const { currentAuthorsListItem } = this.elements;
        if (!this.hiddenItems) {
            button.classList.add(this.classList.hide);
        }
        button.addEventListener('click', this.onClick.bind(this, currentAuthorsListItem));
        if (button.classList.contains(this.classList.authorsButton)) {
            this.onViewPortChange(button, currentAuthorsListItem);
        }
    }

    onClick(authorsListItem: HTMLElement, event: Event) {
        event.preventDefault();
        this.elements.currentAuthorsListItem = authorsListItem;
        const button = this.elements.toggleButton;
        const between = this.elements.inBetweenItem;
        const { truncated, hide } = this.classList;

        if (button) {
            if (button.classList.contains(truncated)) {
                this.elements.listItems.forEach(item => {
                    item.classList.remove(hide);
                });
                button.textContent = this.truncatedButtonText;
            } else {
                this.truncate();
                button.textContent = this.buttonText;
            }
            button.classList.toggle(truncated);
        }
        if (between) {
            between.classList.toggle(hide);
        }
    }


    onViewPortChange(button: HTMLElement, item: HTMLElement) {
        const viewPort = this.elements.viewport;
        for (let key in viewPort) {
            if (viewPort[key]) {
                onVPort(key as ScreenSize, this.onViewPortTruncate.bind(this, button, item))
                offVPort(key as ScreenSize, this.onViewPortTruncate.bind(this, button, item))
            }
        }
    }

    onViewPortTruncate(button: HTMLElement, item: HTMLElement) {
        if (button.classList.contains(this.classList.truncated)) {
            this.elements.currentAuthorsListItem = item;
            this.truncate();
            button.textContent = this.buttonText;
        }
        this.toggleButtonVisibility(button);
    }

    toggleButtonVisibility(button: HTMLElement) {
        const { hide } = this.classList;
        if (this.hiddenItems) {
            button.classList.remove(hide);
        } else {
            button.classList.add(hide);
        }
    }


    truncate() {
        const items = this.elements.listItems;
        const maxItems = this.elements.maxLength;
        let lastIndex = items.length - 1;
        const lastItem = items[lastIndex];
        let showDots = !!this.elements.showMiddleDots;
        lastIndex = showDots ? lastIndex : lastIndex + 1;
        this.toggleAuthorItemClass(items, maxItems, lastIndex);
        this.addSeperator(showDots, lastItem, items);
        return lastItem;
    }

    toggleAuthorItemClass(items: NodeListOf<HTMLElement>, maxItems: number, lastIndex: number) {
        const { hide } = this.classList;
        this.hiddenItems = 0;
        items.forEach((item, index) => {
            if (index > maxItems && index !== lastIndex) {
                item.classList.add(hide);
                this.hiddenItems++;
            } else {
                item.classList.remove(hide);
            }
        });
    }

    addSeperator(showDots: boolean, lastItem: HTMLElement, items: NodeListOf<HTMLElement>) {
        if (showDots && !this.elements.inBetweenItem) {
            lastItem?.insertAdjacentHTML('beforebegin', this._inBetweenItemHTML);

            if (!this.hiddenItems && items.length > 0) {
                this.elements.currentAuthorsListItem.querySelector(this.selectors.between).classList.add(this.classList.hide);
            }
        }
    }


}

export default AuthorsTruncate;

export {
    Elements as AuthorsTruncateElements,
    Selectors as AuthorsTruncateSelectors,
    ClassList as AuthorsTruncateClassList,
    Texts as AuthorsTruncateTexts
}